import { z } from 'zod';
/** MS-4's states, and Closed for a milestone a member closed (MS-2). */
export const MILESTONE_STATES = ['On track', 'At risk', 'Late', 'Done', 'Closed'];
/** MS-5: a milestone lists its items eight to a page. */
export const MILESTONE_ITEMS_PER_PAGE = 8;
const milestoneItem = z.object({
    /** `story:<repository>/<id>`, `task:<project>/<id>` or `<repository>#<number>`. */
    key: z.string(),
    ref: z.string(),
    title: z.string(),
    url: z.string(),
    /** Its state as its source words it. */
    state: z.string(),
    done: z.boolean(),
    /** Whom it is assigned to, by first name; empty when nobody. */
    who: z.string(),
    /** Talos, or the member who added it (MS-3). */
    assignedBy: z.string(),
    /** When it was assigned, e.g. "24 Sep". */
    assignedOn: z.string(),
});
const milestone = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    /** YYYY-MM-DD, and as the page words it: "Fri 16 Oct". */
    date: z.string(),
    dateLabel: z.string(),
    source: z.enum(['Pulse', 'Teamwork']),
    state: z.enum(MILESTONE_STATES),
    done: z.number(),
    total: z.number(),
    /** MS-4's forecast, or null with no rate or nothing remaining. */
    forecast: z.string().nullable(),
    forecastLabel: z.string(),
    /** Talos's line on the state, when it has written one. */
    talosLine: z.string().nullable(),
    /** Still to do first, then done (MS-5). */
    items: z.array(milestoneItem),
    /** The latest change, e.g. "Edited by Gaurav on 24 Sep"; null for a Teamwork milestone. */
    lastChange: z.string().nullable(),
    /** False for a Teamwork milestone, which follows Teamwork (MS-2). */
    editable: z.boolean(),
});
/** A project's Milestones tab (MS-1 to MS-5), as of now. */
export const milestonesResponseSchema = z.object({
    /** For the project's tab row (PRJ-1). */
    product: z.object({ slug: z.string(), name: z.string(), hasBoard: z.boolean(), hasStories: z.boolean() }),
    /** Open milestones by date, then closed ones. */
    milestones: z.array(milestone),
    /** MS-4's rate: the project's items finished per week over the last 4 weeks. */
    ratePerWeek: z.number(),
});
const projectRef = z.object({ slug: z.string(), name: z.string() });
/** MS-6: every project's milestones on one page, as of now. */
export const milestonesOverviewResponseSchema = z.object({
    /** Open milestones, soonest first. */
    milestones: z.array(z.object({
        id: z.string(),
        title: z.string(),
        project: projectRef,
        source: z.enum(['Pulse', 'Teamwork']),
        state: z.enum(MILESTONE_STATES),
        /** YYYY-MM-DD, and as the page words it: "Fri 16 Oct". */
        date: z.string(),
        dateLabel: z.string(),
        forecastLabel: z.string(),
        done: z.number(),
        total: z.number(),
        talosLine: z.string().nullable(),
        /** The first two items still to do, and how many are left. */
        nextItems: z.array(z.object({ ref: z.string(), title: z.string(), url: z.string() })),
        remaining: z.number().int(),
        /** Due within the next three weeks. */
        dueSoon: z.boolean(),
    })),
    /** Closed milestones, by title. */
    closed: z.array(z.object({ id: z.string(), title: z.string(), project: projectRef })),
    /** The projects with a milestone, for the filter. */
    projects: z.array(projectRef),
    /** The navigation badge: open milestones late or at risk. */
    attention: z.number().int(),
});
/** Creating or editing a Pulse milestone (MS-2). */
export const milestoneBodySchema = z.object({
    title: z.string().trim().min(1, 'Give the milestone a title.').max(120, 'A title is up to 120 characters.'),
    description: z.string().trim().max(2000, 'A description is up to 2,000 characters.').default(''),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Give the milestone a date.'),
});
/** Adding an item to a milestone (MS-3). */
export const milestoneItemBodySchema = z.object({ key: z.string().min(1) });
/** The project's items a member can add to a milestone, matching a search (MS-3). */
export const milestoneCandidatesResponseSchema = z.object({
    items: z.array(z.object({ key: z.string(), ref: z.string(), title: z.string(), state: z.string(), milestone: z.string().nullable() })),
});
//# sourceMappingURL=milestones.js.map